import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from "next-themes";
import { Settings, HelpCircle, LogOut, Moon, Sun } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CurrentUserAvatar } from '@/components/auth/CurrentUserAvatar';
import { useUserRole } from '@/hooks/useUserRole';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase/client';

interface UserDropdownProps {
  // Pas besoin de props ici car les hooks useAuth et useUserRole sont utilisés directement
}

const UserDropdown: React.FC<UserDropdownProps> = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { role } = useUserRole();
  const { theme, setTheme } = useTheme();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/auth/login');
  };

  const toggleTheme = async (e: React.MouseEvent) => {
    e.preventDefault();
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);

    if (user) {
      try {
        const { error } = await supabase
          .from("profiles")
          .upsert({
            id: user.id,
            theme: newTheme,
            updated_at: new Date().toISOString(),
          }, { onConflict: 'id' });

        if (error) {
          console.error("Error updating theme:", error);
        }
      } catch (err) {
        console.error("Unexpected error updating theme:", err);
      }
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <div className="relative h-10 w-10 rounded-full hover:opacity-80 transition-all duration-300 cursor-pointer">
          <CurrentUserAvatar className="h-10 w-10 border-2 border-primary/20 shadow-lg" />
          <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-emerald-500 rounded-full border-2 border-background shadow-[0_0_8px_rgba(16,185,129,0.5)]" />
        </div>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className="w-64 backdrop-blur-xl bg-background/95 border border-border/50 shadow-2xl animate-scale-in p-2"
        align="end"
        forceMount
      >
        <DropdownMenuLabel className="font-normal p-3 rounded-lg bg-gradient-to-r from-white/5 to-transparent mb-1">
          <div className="flex items-center gap-3">
            <CurrentUserAvatar className="h-10 w-10 border-2 border-primary/30 shadow-lg" />
            <div className="flex flex-col gap-0.5">
              <p className="text-sm font-semibold leading-none">{user?.email}</p>
              <p className="text-xs leading-none text-muted-foreground">
                {role === 'superadmin' ? 'Super Administrateur' : 'Administrateur'}
              </p>
            </div>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-border/50 my-2" />
        <DropdownMenuItem
          onClick={toggleTheme}
          className="hover:bg-primary/15 transition-all duration-200 cursor-pointer rounded-lg py-2.5 px-3 group justify-between"
        >
          <div className="flex items-center">
            {theme === "light" ? (
              <Moon className="mr-3 h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
            ) : (
              <Sun className="mr-3 h-4 w-4 text-muted-foreground group-hover:text-warning transition-colors" />
            )}
            <span>Thème</span>
          </div>
          <span className="text-xs text-muted-foreground group-hover:text-primary transition-colors">
            {theme === "light" ? "Clair" : "Sombre"}
          </span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/user/settings')}
          className="hover:bg-primary/15 transition-all duration-200 cursor-pointer rounded-lg py-2.5 px-3 group"
        >
          <Settings className="mr-3 h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          <span>Paramètres</span>
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => navigate('/aide')}
          className="hover:bg-primary/15 transition-all duration-200 cursor-pointer rounded-lg py-2.5 px-3 group"
        >
          <HelpCircle className="mr-3 h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
          <span>Aide</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className="bg-border/50 my-2" />
        <DropdownMenuItem
          onClick={handleSignOut}
          className="hover:bg-destructive/15 text-destructive transition-all duration-200 cursor-pointer rounded-lg py-2.5 px-3 group"
        >
          <LogOut className="mr-3 h-4 w-4" />
          <span>Se déconnecter</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserDropdown;
